import {
  applyCancelSplit,
  applyNoShow,
  applyPlanCancel,
  isCancellableNow,
  isLiveBooking,
  isPastBooking,
  type CancelDepositOutcome,
} from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { eventBus } from '@/events/eventBus';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { intentFailureCopy, type IntentFailure } from '@/api/intents';
import { cancelIntent, type CancelIntentPayload } from '@/api/opsIntents';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { flushWritesAndWait, isWritePending } from '@/state/writeQueue';
import { invalidateWriteKeys } from '@/state/reactivityContract';
import { queryClient } from '@/api/queryClient';
import type { Booking, Deposit, Persona, TopupPlan } from '@/domain/types';

/**
 * The ONE Ops cancel 7-day split (W5 m9), shared with the server intent
 * (POST /api/intents/ops-cancel) via @atink/domain. Re-exported here for the
 * Confirmations flow (W4-C's TODO) and Track B.
 */
export { applyCancelSplit };

// ---------------------------------------------------------------------------
// Ops cancel (vault System Diagram 09). Ops is the only role that can cancel a
// booking (enforced at the call site). A cancel always carries a typed reason,
// frees the slot so the Sweeper can rebook it, and applies the deposit fork:
// inside 7 days the deposit forfeits to the house, outside 7 days it is kept on
// file. Both outcomes route to the Sweeper. Mutations follow the *Flow.ts
// convention: read store, mutate via store actions, log, publish. "Now" is the
// sim clock, never Date.now().
// ---------------------------------------------------------------------------

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/** One Melbourne-calendar-day boundary for the client and server, regardless of host timezone. */
export { CANCEL_WINDOW_DAYS as OPS_CANCEL_WINDOW_DAYS, isInsideCancelWindow } from '@atink/domain';
export type OpsCancelDepositOutcome = CancelDepositOutcome;

export interface OpsCancelInput {
  bookingId: string;
  /** Mandatory typed reason - Ops cancels are always accountable. */
  reason: string;
  /** Optional picklist code logged beside the typed reason (e.g. 'customer-requested'). */
  reasonCode?: string;
  /** Persona / person cancelling (Ops). */
  by: string;
  /** The surface raising it (customer-record, ops, confirmations, topup-chase, console...). */
  source?: string;
  /** The confirmation call this cancel resolves: stamps a 'cancelled' touch + the call outcome. */
  callId?: string;
  at?: string;
}

/**
 * Who may cancel a booking (DECIDED 25 Sep, Q4): ONLY Ops — Georgina, plus the
 * owner who holds every Ops clearance. Everyone else gets "Ask Ops to cancel"
 * (askOpsToCancel: an Ops DM with the booking link). Client-side until real
 * auth lands (tv-1vh.13). A null persona (tests / no session) is not Ops.
 */
export function canCancelBooking(persona: Persona | null | undefined): boolean {
  return persona === 'georgina' || persona === 'owner';
}

export interface OpsCancelResult {
  ok: boolean;
  /** Served build: await before reporting success. `ok` only accepts the local request. */
  completion?: Promise<boolean>;
  bookingId?: string;
  depositOutcome?: OpsCancelDepositOutcome;
  /** Present when a paid deposit was kept on file (outside 7 days): the surfaced deposit id. */
  surfacedDepositId?: string;
  error?: 'no-booking' | 'missing-reason' | 'already-cancelled' | 'not-cancellable' | 'past-booking';
}

/**
 * Ops cancels a booking (W5 m9). A typed reason is mandatory (a blank reason is
 * refused with no mutation). `applyCancelSplit` decides everything:
 *  - the booking -> cancelled with cancelReason / cancelledBy / cancelledAt;
 *  - inside 7 days a paid deposit -> 'forfeited' (+ a forfeit ledger movement);
 *  - outside 7 days it is kept on file (unlinked + 12-month validity) -> the
 *    Sweeper's deposit-no-date pool;
 *  - an unpaid link on the held slot is voided;
 *  - its active top-up plan is cancelled and the unpaid instalments voided
 *    (DECIDED 24 Sep, Q2, tv-2na.8.16) — the paid ones follow the split above;
 *  - a call behind it stamps a 'cancelled' touch.
 * Served build: ONE atomic intent (POST /api/intents/ops-cancel); the store is
 * patched optimistically WITHOUT the bridge PUTs and a refusal restores it. The
 * store fallback (tests / standalone) writes through the store. Once cancelled,
 * the booking is no longer live, so its Ops worklist row goes (isLiveBooking).
 */
export function opsCancelBooking(input: OpsCancelInput): OpsCancelResult {
  return cancelBooking({ ...input, source: input.source ?? 'ops' });
}

/**
 * THE client entry for a cancel (W5b #1, tv-2na.12.17): the shared 7-day split,
 * one atomic POST /api/intents/cancel in the served build. A past, never-attended
 * booking is refused ('past-booking') — mark it a no-show (markNoShowBooking).
 */
export function cancelBooking(input: OpsCancelInput): OpsCancelResult {
  const brain = useBrainStore.getState();
  if (!input.reason.trim()) return { ok: false, error: 'missing-reason' };
  const booking = brain.bookings[input.bookingId];
  if (!booking) return { ok: false, error: 'no-booking' };

  const at = input.at ?? nowIso();
  const deposit = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  const plan = activeTopupPlanFor(brain.topupPlans, booking.id, deposit?.id);
  const split = applyCancelSplit<Booking, Deposit, TopupPlan>({
    booking,
    deposit,
    ...(plan ? { plan } : {}),
    reason: input.reason,
    by: input.by,
    atIso: at,
    ...(input.callId ? { callId: input.callId } : {}),
  });
  if (!split.ok) return { ok: false, error: split.error };
  const reason = split.booking.cancelReason!;
  const depositOutcome = split.depositOutcome;
  const surfacedDepositId = depositOutcome === 'kept-on-file' ? deposit?.id : undefined;

  const apply = () => {
    const b = useBrainStore.getState();
    b.upsertBooking(split.booking);
    if (split.deposit) b.upsertDeposit(split.deposit);
    if (split.plan) b.upsertTopupPlan(split.plan);
  };
  let completion: Promise<boolean> | undefined;
  if (isStoreFallbackEnabled()) {
    apply();
  } else {
    completion = syncViaServer(booking, deposit, split.plan ? plan : undefined, apply, "Couldn't cancel the booking", 'cancelled', {
      bookingId: booking.id,
      mode: 'cancel',
      reason,
      by: input.by,
      ...(input.reasonCode ? { reasonCode: input.reasonCode } : {}),
      ...(input.source ? { source: input.source } : {}),
      ...(input.callId ? { callId: input.callId } : {}),
      nowIso: at,
    });
  }

  const recordSuccess = () => {
    brain.appendActivity({
      id: `act-ops-cancel-${booking.id}`,
      type: 'booking-cancelled',
      customerId: booking.customerId,
      studioId: booking.studioId,
      artistId: booking.artistId,
      body: `Ops cancelled the booking - "${reason}". ${
        depositOutcome === 'forfeited'
          ? 'Deposit forfeited to the house (inside 7 days).'
          : depositOutcome === 'kept-on-file'
            ? 'Deposit kept on file (outside 7 days).'
            : depositOutcome === 'link-voided'
              ? 'Unpaid deposit link voided.'
              : 'No deposit on file.'
      } Slot freed to the Sweeper.`,
      timestamp: at,
    });

    eventBus.publish('ops:booking-cancelled', {
      bookingId: booking.id,
      customerId: booking.customerId,
      studioId: booking.studioId,
      depositOutcome,
      surfacedDepositId,
      at,
    });
  };
  if (completion) completion = completion.then((ok) => {
    if (ok) recordSuccess();
    return ok;
  });
  else recordSuccess();

  return { ok: true, bookingId: booking.id, depositOutcome, surfacedDepositId, completion };
}

/**
 * The booking's ACTIVE top-up plan — by its deposit, else by the booking (a
 * dateless link) — the same one the server's runOpsCancel reads.
 */
export function activeTopupPlanFor(
  plans: Record<string, TopupPlan>,
  bookingId: string,
  depositId: string | undefined,
): TopupPlan | undefined {
  const active = Object.values(plans).filter((p) => p.status === 'active');
  return (
    (depositId ? active.find((p) => p.depositId === depositId) : undefined) ?? active.find((p) => p.bookingId === bookingId)
  );
}

/**
 * Served build: the cancel / no-show is ONE server transaction. The changed
 * booking, deposit and top-up plan are mirrored into the store WITHOUT bridge
 * PUTs (the server writes them); a refusal restores them (still without PUTs)
 * and tells Ops why.
 */
function syncViaServer(
  original: Booking | undefined,
  prevDeposit: Deposit | undefined,
  prevPlan: TopupPlan | undefined,
  apply: () => void,
  failTitle: string,
  verb: string,
  payload: CancelIntentPayload,
): Promise<boolean> {
  const pairs: Array<readonly [string, string]> = [
    ...(original ? ([['bookings', original.id]] as const) : []),
    ...(prevDeposit ? ([['deposits', prevDeposit.id]] as const) : []),
    ...(prevPlan ? ([['topupPlans', prevPlan.id]] as const) : []),
  ];
  withServerAuthoredWrites(pairs, apply);

  const revert = (failure: IntentFailure) => {
    withServerAuthoredWrites(pairs, () => {
      useBrainStore.setState((s) => ({
        bookings: original ? { ...s.bookings, [original.id]: original } : s.bookings,
        deposits: prevDeposit ? { ...s.deposits, [prevDeposit.id]: prevDeposit } : s.deposits,
        topupPlans: prevPlan ? { ...s.topupPlans, [prevPlan.id]: prevPlan } : s.topupPlans,
      }));
    });
    if (original) invalidateWriteKeys(queryClient, 'bookings', original);
    const retry = intentFailureCopy(failure).retry;
    useUIStore.getState().pushNotification({
      intensity: 'critical',
      title: failTitle,
      body: retry
        ? `We couldn't reach the server, so nothing was ${verb}. Try again.`
        : `The server refused it: ${failure.message ?? failure.code ?? failure.error}. Nothing was ${verb}.`,
      ttlMs: 10000,
    });
  };

  // L5 (q44j #1): a save of these records still queued (the 3rd chase "No answer"
  // counts on the plan just before it cancels) goes FIRST. Sent after the cancel
  // it reached a closed plan and was refused ("Not saved — This top-up plan is
  // already cancelled") although the cancel itself had gone through. Only when
  // one is queued, so an idle cancel still fetches straight away.
  const queued = pairs.some(([entity, id]) => isWritePending(entity, id));
  const send = queued ? flushWritesAndWait().then(() => cancelIntent(payload)) : cancelIntent(payload);
  return send
    .then((res) => {
      if (!res.ok) revert(res);
      else void queryClient.invalidateQueries({ queryKey: ['ops'] });
      return res.ok;
    })
    .catch((err) => {
      console.error('[cancel] send failed', err);
      revert({ ok: false, kind: 'transient', status: 0, error: 'network' });
      return false;
    });
}

// ---------------------------------------------------------------------------
// No-show (DECIDED 25 Sep, Q5 + Q6): through the SAME cancel intent, mode 'no-show'.
// ---------------------------------------------------------------------------

export interface NoShowInput {
  bookingId: string;
  reason: string;
  reasonCode?: string;
  by: string;
  source?: string;
  at?: string;
}

export interface NoShowResult {
  ok: boolean;
  completion?: Promise<boolean>;
  depositOutcome?: 'forfeited' | 'none';
  amountAud?: number;
  error?: 'no-booking' | 'missing-reason' | 'already-no-show' | 'not-no-showable' | 'not-yet-due';
}

/**
 * Mark a started, never-attended booking no-show: booking -> no-show, any money
 * in forfeited (a `forfeit` movement with the typed reason; the deposit stays
 * linked and reads lapsed), its top-up plan cancelled. @atink/domain applyNoShow
 * decides; the served build sends ONE POST /api/intents/cancel {mode:'no-show'}.
 */
export function markNoShowBooking(input: NoShowInput): NoShowResult {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return { ok: false, error: 'no-booking' };
  const at = input.at ?? nowIso();
  const deposit = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  const plan = activeTopupPlanFor(brain.topupPlans, booking.id, deposit?.id);
  const res = applyNoShow<Booking, Deposit, TopupPlan>({
    booking,
    deposit,
    ...(plan ? { plan } : {}),
    reason: input.reason,
    by: input.by,
    atIso: at,
  });
  if (!res.ok) return { ok: false, error: res.error };
  const apply = () => {
    const b = useBrainStore.getState();
    b.upsertBooking(res.booking);
    if (res.deposit) b.upsertDeposit(res.deposit);
    if (res.plan) b.upsertTopupPlan(res.plan);
  };
  let completion: Promise<boolean> | undefined;
  if (isStoreFallbackEnabled()) apply();
  else
    completion = syncViaServer(booking, res.deposit ? deposit : undefined, res.plan ? plan : undefined, apply, "Couldn't mark the no-show", 'changed', {
      bookingId: booking.id,
      mode: 'no-show',
      reason: input.reason.trim(),
      by: input.by,
      ...(input.reasonCode ? { reasonCode: input.reasonCode } : {}),
      ...(input.source ? { source: input.source } : {}),
      nowIso: at,
    });
  const recordSuccess = () => brain.appendActivity({
    id: `act-no-show-${booking.id}`,
    type: 'cancellation',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `Marked no-show - "${input.reason.trim()}". ${
      res.depositOutcome === 'forfeited' ? `$${res.forfeitedAud} deposit forfeited.` : 'No deposit on file.'
    }`,
    timestamp: at,
  });
  if (completion) completion = completion.then((ok) => {
    if (ok) recordSuccess();
    return ok;
  });
  else recordSuccess();
  return { ok: true, depositOutcome: res.depositOutcome, amountAud: res.forfeitedAud, completion };
}

// ---------------------------------------------------------------------------
// Cancel by plan (the missed top-up chase, tv-2na.12.17 (e)).
// ---------------------------------------------------------------------------

/**
 * Cancel a top-up plan through the one cancel intent. A plan with a live booking
 * cancels that booking (the 7-day split, which cancels the plan with it); a
 * DATELESS plan is cancelled on its own and its paid part stays on file 12 months.
 */
export function cancelTopupPlanById(
  planId: string,
  opts: { reason: string; by: string; source?: string; at?: string },
): { ok: boolean; error?: string; depositOutcome?: CancelDepositOutcome; completion?: Promise<boolean> } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false, error: 'no-plan' };
  const deposit = brain.deposits[plan.depositId];
  const bookingId = plan.bookingId ?? deposit?.bookingId ?? deposit?.heldBookingId;
  const booking = bookingId ? brain.bookings[bookingId] : undefined;
  if (booking && booking.status !== 'superseded') {
    const res = cancelBooking({ bookingId: booking.id, reason: opts.reason, by: opts.by, source: opts.source, at: opts.at });
    return res.ok ? { ok: true, depositOutcome: res.depositOutcome, completion: res.completion } : { ok: false, error: res.error };
  }
  const at = opts.at ?? nowIso();
  const res = applyPlanCancel<Deposit, TopupPlan>({ plan, deposit, atIso: at });
  if (!res.plan) return { ok: true, depositOutcome: 'none' };
  const apply = () => {
    const b = useBrainStore.getState();
    if (res.deposit) b.upsertDeposit(res.deposit);
    b.upsertTopupPlan(res.plan!);
  };
  let completion: Promise<boolean> | undefined;
  if (isStoreFallbackEnabled()) apply();
  else
    completion = syncViaServer(undefined, res.deposit ? deposit : undefined, plan, apply, "Couldn't cancel the top-up plan", 'cancelled', {
      planId,
      mode: 'cancel',
      reason: opts.reason,
      by: opts.by,
      ...(opts.source ? { source: opts.source } : {}),
      nowIso: at,
    });
  return { ok: true, depositOutcome: res.depositOutcome, completion };
}

// ---------------------------------------------------------------------------
// The cancel modal's picklists + "Ask Ops to cancel" (Q4).
// ---------------------------------------------------------------------------

/**
 * A customer's bookings split for the cancel modal: `cancellable` — live and
 * not past (the cancel intent would take them); `noShow` — past, never attended
 * (offer "Mark no-show", never cancel — Q5). Soonest first.
 */
export function cancellableBookingsFor(customerId: string, atIso: string): { cancellable: Booking[]; noShow: Booking[] } {
  const brain = useBrainStore.getState();
  const customer = brain.customers[customerId];
  const ids = new Set(customer?.bookings ?? []);
  for (const b of Object.values(brain.bookings)) if (b.customerId === customerId) ids.add(b.id);
  const mine = [...ids]
    .map((id) => brain.bookings[id])
    .filter((b): b is Booking => Boolean(b) && isLiveBooking(b!))
    .sort((a, b) => a.start.localeCompare(b.start));
  return {
    cancellable: mine.filter((b) => isCancellableNow(b, atIso)),
    noShow: mine.filter(
      (b) => (b.status === 'held' || b.status === 'pending-deposit' || b.status === 'confirmed') && isPastBooking(b.start, atIso),
    ),
  };
}

/** The Ops person a non-Ops persona asks: the operations lead on file, else Georgina. */
function opsPersonId(): string {
  const lead = Object.values(useBrainStore.getState().agents).find((a) => a.role === 'operations-lead');
  return lead?.id ?? 'georgina';
}

/**
 * "Ask Ops to cancel" (DECIDED 25 Sep, Q4): a non-Ops persona never cancels —
 * they DM Ops with the booking link (and their note). Nothing about the booking
 * changes; Ops runs the cancel intent from the link.
 */
export function askOpsToCancel(input: {
  bookingId: string;
  fromPersonId: string;
  note?: string;
  at?: string;
}): { ok: boolean; threadId?: string } {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return { ok: false };
  const at = input.at ?? nowIso();
  const to = opsPersonId();
  const customer = brain.customers[booking.customerId];
  const who = customer ? `${customer.firstName} ${customer.lastName}`.trim() : 'this client';
  const when = new Date(booking.start).toLocaleString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
  const note = input.note?.trim();
  const body = `Please cancel ${who}'s booking on ${when}.${note ? ` ${note}` : ''}`;
  const threadId = brain.openOrCreateDm([input.fromPersonId, to], input.fromPersonId, at);
  brain.sendDmMessage(threadId, body, {
    authorId: input.fromPersonId,
    timestamp: at,
    mentions: [to],
    attachments: [
      { kind: 'link', url: `/customer/${booking.customerId}?booking=${booking.id}`, label: 'Booking to cancel' },
    ],
  });
  return { ok: true, threadId };
}
